import axios from 'axios';
import { useEffect, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiUser,
  FiUsers
} from "react-icons/fi";
import { NotificationManager } from "react-notifications";
import ActionsMenu from '../../../components/ActionsMenu';
import DeleteConfirmationModal from '../../../components/ConfirmationModals/DeleteConfirmationModal';
import Loader from '../../../components/Loader';
import useAuth from '../../../hooks/useAuth';
import { BASE_URL } from '../../../utils/Element';
import EditLeadModal from '../EditLeadModal';
import ConvertPipelineModal from './ConvertPipelineModal';


const LeadsDashboard = ({ dashboardData, onPageChange }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [disableDelete, setDisableDelete] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [selectedLeadType, setSelectedLeadType] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [config] = useAuth()

  useEffect(() => {
    if (
      dashboardData?.data?.sections &&
      Object.keys(expandedSections).length === 0
    ) {
      const initialExpanded = {};

      dashboardData.data.sections.forEach((section, idx) => {
        initialExpanded[idx] = section.is_expanded ?? true;
      });

      setExpandedSections(initialExpanded);
    }
  }, [dashboardData]);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Ultra safe string converter
  const getString = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'object') {
      return value.primary ||
        value.name ||
        value.formatted ||
        value.value ||
        value.label ||
        Object.values(value).find(v => typeof v === 'string') ||
        JSON.stringify(value).replace(/[\{\}""]/g, '');
    }
    return String(value);
  };

   const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/markAs-Proposal/${selectedId}`, { mark_as_proposal: true }, config)
      .then((res) => {
        setShowConfirmModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-lead/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }


  const leadTypeValue =
    selectedLeadType === 0 ? "Disposition" : "Acquisition";

  if (!dashboardData?.success || !dashboardData?.data) {
    return (
      <Loader />
    );
  }

  const { round_robin, overview, sections = [] } = dashboardData.data;

  return (
    <div className="space-y-3 mb-6">
      {/* Round Robin */}
      <div className="bg-white border rounded-2xl p-3">
        <div className="flex items-center justify-between mb-4">
          <h2 className="head-5 dark-H">ROUND ROBIN ORDER</h2>
          <div className="head-5">Next Up</div>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2">
          {round_robin?.order?.map((advisor, idx) => (
            <div
              key={advisor.user_id || idx}
              className={`flex-1 min-w-[140px] p-4 rounded-xl border ${advisor.is_next_up ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center body-N font-medium">
                  {advisor.position}
                </div>
                <div>
                  <div className="body-N">{getString(advisor.name)}</div>
                  {advisor.is_next_up && <div className="head-5 green-H">NEXT UP</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
        {[
          { icon: FiDatabase, count: overview?.unassigned_bds_leads, label: "Unassigned BDS Leads", color: "text-green-600" },
          { icon: FiUsers, count: overview?.advisor_generated_leads, label: "Advisor-Generated Leads", color: "text-blue-600" },
          { icon: FiClock, count: overview?.assigned_bds_leads_0_90_days, label: "Assigned Leads (0-90)", color: "text-orange-600" },
          { icon: FiAlertTriangle, count: overview?.older_leads_90_plus_days, label: "Older Leads (>90)", color: "text-purple-600" },
          { icon: FiCheckCircle, count: overview?.in_pipeline_converted, label: "In Pipeline (Converted)", color: "text-emerald-600" },
        ].map((item, i) => (
          <div key={i} className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <item.icon className={`w-6 h-6 ${item.color}`} />
              <div className="head-2 dark-H">{item.count ?? 0}</div>
            </div>
            <div className="mt-4 body-N">{item.label}</div>
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {sections.map((section, idx) => (
          <div key={section.key || idx} className="bg-white border rounded-2xl overflow-hidden">
            <div
              className={`px-4 py-4 flex items-center justify-between border-b cursor-pointer ${section.accent === 'green' ? 'bg-green-50 border-green-200' :
                section.accent === 'blue' ? 'bg-blue-50 border-blue-200' :
                  section.accent === 'orange' ? 'bg-orange-50 border-orange-200' :
                    'bg-purple-50 border-purple-200'
                }`}
              onClick={() => toggleSection(idx)}
            >
              <div>
                <div className="head-5 green-H">{getString(section.title)}</div>
                <div className="body-S text-gray-500 mt-0.5">{getString(section.description)}</div>
              </div>

              <div className="flex items-center gap-6">
                <div className="text-emerald-600 hover:underline cursor-pointer text-sm font-medium">
                  {getString(section.view_all_label)}
                </div>
                {expandedSections[idx] ? <FiChevronUp /> : <FiChevronDown />}
              </div>
            </div>

            {expandedSections[idx] && (
              <div className="h-[420px] overflow-auto border-t">
                <table className="w-full min-w-[500px] border-collapse">
                  <thead className="sticky top-0 z-20 bg-[#d1dbcf]">
                    <tr className="text-left uppercase body-N dark-M">
                      <th className="px-8 py-4 w-28">LEAD DATE</th>
                      <th className="px-4 py-4 w-56">CONTACT / COMPANY</th>
                      <th className="px-4 py-4 w-80">PROPERTY / INTEREST</th>
                      <th className="px-4 py-4 w-44">LEAD SOURCE</th>
                      <th className="px-4 py-4 w-40">
                        {section.key === 'unassigned_bds_leads' ? 'BDS' : 'LEAD OWNER'}
                      </th>
                      <th className="px-4 py-4 w-40">LAST CONTACTED</th>
                      <th className="px-8 py-4 text-right w-28">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="body-N dark-M">
                    {section.items?.length > 0 ? (
                      section.items.map((lead) => {
                        const contactName = getString(lead.contact);
                        const contactCompany = getString(lead.contact?.secondary || lead.company);

                        const owner = section.key === 'unassigned_bds_leads' ? lead.bds : lead.lead_owner;
                        const ownerName = getString(owner);

                        return (
                          <tr key={lead.id || Math.random()} className="border-t hover:bg-gray-50">
                            <td className="px-8 py-5 font-medium text-gray-700 whitespace-nowrap">
                              {getString(lead.lead_date_formatted || lead.lead_date)}
                            </td>

                            <td className="px-4 py-5 text-wrap">
                              <div className="font-medium flex items-center gap-2">
                                <FiUser className="text-gray-400 flex-shrink-0" />
                                {contactName}
                              </div>
                              {contactCompany && (
                                <div className="text-xs text-gray-500 mt-0.5 pl-6">
                                  {contactCompany}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-5 text-gray-700 whitespace-pre-line">
                              {getString(lead.property_or_acquisition)}
                            </td>

                            <td className="px-4 py-5 text-gray-600">
                              {getString(lead.lead_source)}
                            </td>

                            <td className="px-4 py-5">
                              <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                                <FiUser className="flex-shrink-0" />
                                {ownerName}
                              </div>
                            </td>

                            <td className="px-4 py-5 text-gray-600 text-sm whitespace-nowrap">
                              {getString(lead.last_contacted)}
                            </td>


                            <td className="flex gap-3 items-center w-full justify-center">
                              <div>
                                <ActionsMenu
                                  from="lead"
                                  handleEdit={() => {
                                    setShowEditModal(true)
                                    setSelectedId(lead?.id)
                                  }}
                                  handleDelete={() => {
                                    setSelectedId(lead?.id)
                                    setShowDeleteModal(true)
                                  }}
                                  showOtherOption={lead?.lead_typetype === 0 ? true : false}
                                  otherOptionTitle={"Convert to Pipeline"}
                                  handleOtherOption={() => {
                                    setShowConfirmModal(true)
                                    setSelectedId(lead?.id)
                                    setSelectedLeadType(lead?.lead_type)
                                  }}

                                />
                              </div>

                            </td>

                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-8 py-16 text-center text-gray-500">
                          No leads found in this section
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {section.pagination?.last_page > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
                    <div className="text-sm text-gray-500">
                      Showing {section.pagination.from} to {section.pagination.to} of{" "}
                      {section.pagination.total}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        disabled={section.pagination.current_page === 1}
                        onClick={() =>
                          onPageChange?.(
                            section.key,
                            section.pagination.current_page - 1
                          )
                        }
                        className="px-3 py-1 border rounded disabled:opacity-50"
                      >
                        Prev
                      </button>

                      <span className="text-sm font-medium">
                        {section.pagination.current_page} /{" "}
                        {section.pagination.last_page}
                      </span>

                      <button
                        disabled={
                          section.pagination.current_page ===
                          section.pagination.last_page
                        }
                        onClick={() => {
                          console.log("clicked");
                          onPageChange(
                            section.key,
                            section.pagination.current_page + 1
                          );
                        }}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <EditLeadModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("")
        }}
        from="Lead"
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      <ConvertPipelineModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        from="lead"
        id={selectedId}
        leadType={leadTypeValue}
      />

    </div>
  );
};

export default LeadsDashboard;